import { ChunkTileLayers } from '../../shared/Chunks'
import { getEntityByName } from '../../shared/Entities'
import { Flags } from '../../shared/Flags'
import { uuid } from '../../shared/uuid'
import { getTilesetDef } from '../../shared/Tilesets'

export class ClientEditor {
  constructor(client) {
    this.client = client
    this.open = false
    this.tab = null
    // Tileset paint state
    this.selectedTilesetId = null
    this.selectedCells = [] // [[cx, cy], ...]
    this.paintLayer = 0
    this.paintCollider = null
    this.paintFps = 0
    this.paintFrames = [] // additional animation frame cells beyond selectedCells[0]
    this.paintType = 'raw' // 'raw' | 'animated' | 'autotile'
    this.paintPresetId = null
    this.paintTool = 'brush' // 'brush' | 'fill'
    this.selectedEntityName = null
    this.selectedFlagName = Object.keys(Flags)[0]
    this.lastPaintedKey = null
    this.eraseLayer = null // layer locked on rmb press for drag-erase
    this.lastErasedKey = null
  }

  setState(state) {
    const {
      open, tab,
      selectedTilesetId, selectedCells, paintLayer, paintCollider,
      selectedEntityName, selectedFlagName,
    } = state

    this.open = open ?? this.open
    this.tab = tab ?? this.tab
    this.selectedTilesetId = selectedTilesetId ?? this.selectedTilesetId
    this.selectedCells = selectedCells ?? this.selectedCells
    this.paintLayer = paintLayer ?? this.paintLayer
    this.paintCollider = paintCollider !== undefined ? paintCollider : this.paintCollider
    this.paintFps = state.paintFps ?? this.paintFps
    this.paintFrames = state.paintFrames ?? this.paintFrames
    this.paintType = state.paintType ?? this.paintType
    this.paintPresetId = state.paintPresetId ?? this.paintPresetId
    this.paintTool = state.paintTool ?? this.paintTool
    this.selectedEntityName = selectedEntityName ?? this.selectedEntityName
    this.selectedFlagName = selectedFlagName ?? this.selectedFlagName
    this.lastPaintedKey = null

    this.client.flagOverlay?.setVisible(this.open && this.tab === 'flags')
  }

  _buildDesc(cx, cy, fps, frames) {
    const url = getTilesetDef(this.selectedTilesetId)?.url ?? this.selectedTilesetId
    const desc = { ts: url, x: cx, y: cy, layer: this.paintLayer }
    if (this.paintCollider) desc.collider = this.paintCollider
    if (fps) desc.fps = fps
    if (frames?.length) desc.frames = frames
    return desc
  }

  _floodFill(startX, startY) {
    const target = this.client.tiles.getStateAt(startX, startY, this.paintLayer)
    // Identify the tile type to replace: match ts+x+y, or null for empty
    const targetKey = target ? `${target.ts},${target.x},${target.y}` : null

    const visited = new Set()
    const queue = [[startX, startY]]
    const MAX_TILES = 500

    while (queue.length && visited.size < MAX_TILES) {
      const [x, y] = queue.shift()
      const key = `${x},${y}`
      if (visited.has(key)) continue
      if (!this.client.chunks.getAtWorld(x, y)) continue
      const state = this.client.tiles.getStateAt(x, y, this.paintLayer)
      const stateKey = state ? `${state.ts},${state.x},${state.y}` : null
      if (stateKey !== targetKey) continue
      visited.add(key)
      queue.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1])
    }

    for (const key of visited) {
      const [wx, wy] = key.split(',').map(Number)
      if (this.paintType === 'autotile' && this.paintPresetId) {
        const changes = this.client.tiles.applyTilesetAutotileAt(
          this.selectedTilesetId, this.paintPresetId, wx, wy
        )
        for (const change of changes) {
          this.client.send({ type: 'editTile', x: change.x, y: change.y, tile: change.tile })
        }
      } else if (this.paintType === 'animated' && this.selectedCells.length > 0) {
        const [cx, cy] = this.selectedCells[0]
        const desc = this._buildDesc(cx, cy, this.paintFps, this.paintFrames)
        this.client.tiles.setTileDescAt(wx, wy, desc)
        this.client.send({ type: 'editTile', x: wx, y: wy, tile: desc })
      } else if (this.selectedCells.length > 0) {
        const [cx, cy] = this.selectedCells[0]
        const desc = this._buildDesc(cx, cy)
        this.client.tiles.setTileDescAt(wx, wy, desc)
        this.client.send({ type: 'editTile', x: wx, y: wy, tile: desc })
      }
    }
  }

  update() {
    if (!this.open) return

    const input = this.client.input
    if (input.lmb.released) {
      this.lastPaintedKey = null
    }
    if (input.rmb.released) {
      this.eraseLayer = null
      this.lastErasedKey = null
    }

    if (this.tab === 'tiles' && input.lmb.down && this.selectedTilesetId) {
      const { x, y } = this.client.getPointerTilePosition()

      if (this.paintTool === 'fill' && input.lmb.pressed) {
        this._floodFill(x, y)
      } else if (this.paintTool === 'brush') {
        const key = `${x},${y},${this.selectedTilesetId},${this.paintType},${this.paintPresetId}`
        if (this.lastPaintedKey === key) return
        this.lastPaintedKey = key

        if (this.paintType === 'autotile' && this.paintPresetId) {
          // Autotile preset — run neighbour detection, update surrounding cells too
          const changes = this.client.tiles.applyTilesetAutotileAt(
            this.selectedTilesetId, this.paintPresetId, x, y
          )
          for (const change of changes) {
            this.client.send({ type: 'editTile', x: change.x, y: change.y, tile: change.tile })
          }
        } else if (this.paintType === 'animated' && this.selectedCells.length > 0) {
          // Animated preset — one tile with all frame data
          const [cx, cy] = this.selectedCells[0]
          const desc = this._buildDesc(cx, cy, this.paintFps, this.paintFrames)
          this.client.tiles.setTileDescAt(x, y, desc)
          this.client.send({ type: 'editTile', x, y, tile: desc })
        } else if (this.selectedCells.length > 0) {
          // Raw stamp — each selected cell at its grid offset
          const anchorCx = Math.min(...this.selectedCells.map(c => c[0]))
          const anchorCy = Math.min(...this.selectedCells.map(c => c[1]))
          for (const [cx, cy] of this.selectedCells) {
            const wx = x + (cx - anchorCx)
            const wy = y - (cy - anchorCy)
            const desc = this._buildDesc(cx, cy)
            this.client.tiles.setTileDescAt(wx, wy, desc)
            this.client.send({ type: 'editTile', x: wx, y: wy, tile: desc })
          }
        }
      }
    }

    if (this.tab === 'objects' && this.selectedEntityName && input.lmb.pressed) {
      const { x, y } = this.client.getPointerTilePosition()
      if (getEntityByName(this.selectedEntityName) && this.client.chunks.getAtWorld(x, y)) {
        this.client.send({ type: 'placeEntity', id: uuid(), name: this.selectedEntityName, x, y })
      }
    }

    if (this.tab === 'objects' && input.rmb.pressed) {
      const { x, y } = this.client.getWorldPointerPosition()
      const hits = this.client.targets.query(x, y, 0)
      const hit = hits.find(h => h.value.id !== this.client.playerId)
      if (hit && this.client.entities.removeEditorEntity(hit.value.id)) {
        this.client.send({ type: 'deleteEntity', entityId: hit.value.id })
      }
    }

    if (this.tab === 'tiles' && input.rmb.down) {
      const { x, y } = this.client.getPointerTilePosition()
      // On initial press, find the topmost occupied layer and lock to it
      if (input.rmb.pressed) {
        this.eraseLayer = null
        for (let i = ChunkTileLayers.length - 1; i >= 0; i -= 1) {
          const layer = ChunkTileLayers[i]
          if (this.client.tiles.getStateAt(x, y, layer)) {
            this.eraseLayer = layer
            break
          }
        }
      }
      if (this.eraseLayer !== null) {
        const key = `${x},${y}`
        if (this.lastErasedKey !== key) {
          this.lastErasedKey = key
          if (this.client.tiles.clearAt(x, y, this.eraseLayer)) {
            this.client.send({ type: 'clearTile', x, y, layer: this.eraseLayer })
          }
        }
      }
    }

    if (this.tab === 'flags' && this.selectedFlagName) {
      const flag = Flags[this.selectedFlagName]
      if (!flag) return

      if (input.lmb.down) {
        const { x, y } = this.client.getPointerTilePosition()
        const key = `${x},${y},${this.selectedFlagName}`
        if (this.lastPaintedKey !== key) {
          this.lastPaintedKey = key
          this.client.send({ type: 'editChunkFlags', x, y, flagId: flag.id, on: true })
          const chunk = this.client.chunks.getAtWorld(x, y)
          if (chunk) {
            chunk.setFlagsAtWorld(x, y, chunk.getFlagsAtWorld(x, y) | flag.id)
            this.client.flagOverlay?.update(chunk)
          }
        }
      }

      if (input.rmb.down) {
        const { x, y } = this.client.getPointerTilePosition()
        const key = `${x},${y},${this.selectedFlagName},clear`
        if (this.lastPaintedKey !== key) {
          this.lastPaintedKey = key
          this.client.send({ type: 'editChunkFlags', x, y, flagId: flag.id, on: false })
          const chunk = this.client.chunks.getAtWorld(x, y)
          if (chunk) {
            chunk.setFlagsAtWorld(x, y, chunk.getFlagsAtWorld(x, y) & ~flag.id)
            this.client.flagOverlay?.update(chunk)
          }
        }
      }
    }
  }
}
